// Copied from the mobile app's features/sky/starLayout.ts with the types removed.
// The web and the phone place protocol stars by the same rules only while the two
// files stay identical, so change them together. The one difference is that stars
// sort by the server's `order` rank instead of a creation date, which the public
// payload does not carry.
import { hashSeed, mulberry32 } from "./random.js";
import { scatterStarAnchors } from "./star_scatter.js";

const MARK_RADIUS = 22;
const EDGE_INSET = 20;
const GROUP_GAP = 18;
const BACKGROUND_STAR_GAP = 34;

function bounds(star, active = true) {
  const radius = active ? MARK_RADIUS : 3;
  return {
    left: star.x - radius,
    right: star.x + radius,
    top: star.y - radius,
    bottom: star.y + radius,
  };
}

function overlap(a, b, gap) {
  const width = Math.min(a.right, b.right) - Math.max(a.left, b.left) + gap;
  const height = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) + gap;
  return Math.max(0, width) * Math.max(0, height);
}

export function layoutStars(
  stars,
  { seed, width, height, horizontalDrift, insets = { top: Math.min(160, height * 0.2), bottom: 32 } }
) {
  const ordered = [...stars].sort((a, b) => {
    const age = (a.order ?? 0) - (b.order ?? 0);
    if (age) return age;
    const difference = hashSeed(seed + "/" + a.id) - hashSeed(seed + "/" + b.id);
    return difference || a.id.localeCompare(b.id);
  });
  const active = ordered.filter((star) => star.active);
  const inactive = ordered.filter((star) => !star.active);
  const position = (x, y) => {
    const left = EDGE_INSET + MARK_RADIUS;
    const right = width - horizontalDrift - EDGE_INSET - MARK_RADIUS;
    const top = insets.top + MARK_RADIUS;
    const bottom = height - insets.bottom - MARK_RADIUS;
    return {
      x: Math.max(left, Math.min(Math.max(left, right), x)),
      y: Math.max(top, Math.min(Math.max(top, bottom), y)),
    };
  };
  const candidate = (rng) => {
    const start = position(0, 0);
    const end = position(width, height);
    return { ...start, x: start.x + rng() * (end.x - start.x), y: start.y + rng() * (end.y - start.y) };
  };
  const fieldWidth = Math.max(0, width - horizontalDrift - EDGE_INSET * 2 - MARK_RADIUS * 2);
  const fieldHeight = Math.max(0, height - insets.top - insets.bottom - MARK_RADIUS * 2);
  const anchors = scatterStarAnchors(
    active.map((star) => star.id),
    seed,
    fieldWidth,
    fieldHeight
  );
  const targets = new Map();
  for (const [index, star] of active.entries()) {
    const anchor = anchors[index];
    targets.set(
      star.id,
      position(EDGE_INSET + MARK_RADIUS + anchor.x * fieldWidth, insets.top + MARK_RADIUS + anchor.y * fieldHeight)
    );
  }

  const placements = new Map(targets);
  const rowHeight = (height - insets.top - insets.bottom) / Math.max(1, Math.ceil(active.length / 2));
  const groupGap = Math.min(GROUP_GAP, Math.max(8, rowHeight - MARK_RADIUS * 2));
  const passes = active.length <= 14 ? 12 : 2;
  for (let pass = 0; pass < passes; pass++) {
    let moved = false;
    for (const star of active) {
      const target = targets.get(star.id);
      const occupied = active.filter((other) => other.id !== star.id).map((other) => bounds(placements.get(other.id)));
      const rng = mulberry32(hashSeed(seed + "/" + star.id + "/adjust/" + pass));
      let best = placements.get(star.id);
      const candidates = [best, target];
      for (const neighbor of occupied) {
        candidates.push(
          position(neighbor.left - groupGap, target.y),
          position(neighbor.right + groupGap, target.y),
          position(target.x, neighbor.top - MARK_RADIUS - groupGap),
          position(target.x, neighbor.bottom + MARK_RADIUS + groupGap)
        );
      }
      let bestScore = Infinity;
      const samples = active.length <= 14 ? 256 : 32;
      for (let sample = 0; sample < candidates.length + samples; sample++) {
        const point =
          candidates[sample] ??
          position(target.x + (rng() - 0.5) * width * 0.6, target.y + (rng() - 0.5) * height * 0.28);
        const box = bounds(point);
        const score =
          occupied.reduce((sum, other) => sum + overlap(box, other, groupGap), 0) * 1000 +
          (point.x - target.x) ** 2 +
          (point.y - target.y) ** 2;
        if (score < bestScore) {
          best = point;
          bestScore = score;
        }
        if (bestScore === 0) break;
      }
      if (best !== placements.get(star.id)) moved = true;
      placements.set(star.id, best);
    }
    if (!moved) break;
  }

  const occupied = active.map((star) => bounds(placements.get(star.id)));
  for (const star of inactive) {
    const rng = mulberry32(hashSeed(seed + "/" + star.id + "/inactive"));
    let best = candidate(rng);
    let bestScore = Infinity;
    for (let sample = 0; sample < 64; sample++) {
      const point = sample === 0 ? best : candidate(rng);
      const box = bounds(point, false);
      const score = occupied.reduce((sum, other) => sum + overlap(box, other, BACKGROUND_STAR_GAP), 0);
      if (score < bestScore) {
        best = point;
        bestScore = score;
      }
      if (score === 0) break;
    }
    placements.set(star.id, best);
    occupied.push(bounds(best, false));
  }
  return placements;
}
